import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Clock, BarChart3, AlertTriangle } from 'lucide-react';
import { api } from '../../../api/client';

export function MetricsTab({ sellerId }: { sellerId: number }) {
  const { t } = useTranslation();
  const [metrics, setMetrics] = useState<any>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const res: any = await api.get(`/warehouse/metrics?seller_id=${sellerId}&days=14`);
        if (res.success) setMetrics(res.data);
      } catch (err) { console.error('Failed to load metrics', err); }
      finally { setLoading(false); }
    })();
  }, [sellerId]);

  if (loading || !metrics) return <div className="py-8 text-center text-[var(--color-text-secondary)]">{t('common.loading')}</div>;

  const fmtTime = (seconds: number) => {
    if (seconds < 60) return `${seconds}s`;
    if (seconds < 3600) return `${Math.round(seconds / 60)}m`;
    return `${(seconds / 3600).toFixed(1)}h`;
  };

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {[
          { label: t('wh.avgProcessTime'), value: fmtTime(metrics.today.avg_seconds), sub: `${t('wh.today')}: ${metrics.today.closed_count} ${t('wh.closed')}`, icon: Clock },
          { label: t('wh.itemsPerHour'), value: String(metrics.today.items_per_hour), sub: t('wh.currentBatch'), icon: BarChart3 },
          { label: t('wh.periodAvg'), value: fmtTime(metrics.period.avg_seconds), sub: `${metrics.period.days}d -- ${metrics.period.closed_count} ${t('wh.items')}`, icon: Clock },
          { label: t('wh.errorRate'), value: `${metrics.period.error_rate_pct}%`, sub: `${metrics.period.alert_count} ${t('wh.alerts')} / ${metrics.period.item_count} ${t('wh.items')}`, icon: AlertTriangle, color: metrics.period.error_rate_pct > 10 ? 'text-red-500' : 'text-green-500' },
        ].map(({ label, value, sub, icon: Icon, color }) => (
          <div key={label} className="bg-[var(--color-bg-card)] border border-[var(--color-border)] rounded-lg p-4">
            <div className="flex items-center gap-2 mb-2">
              <Icon size={16} className="text-[var(--color-text-secondary)]" />
              <span className="text-xs text-[var(--color-text-secondary)]">{label}</span>
            </div>
            <div className={`text-2xl font-bold ${color || 'text-[var(--color-text)]'}`}>{value}</div>
            <div className="text-xs text-[var(--color-text-secondary)] mt-1">{sub}</div>
          </div>
        ))}
      </div>

      {metrics.daily?.length > 0 && (
        <div className="bg-[var(--color-bg-card)] border border-[var(--color-border)] rounded-lg p-4">
          <h3 className="text-sm font-medium text-[var(--color-text)] mb-3">{t('wh.dailyItems')}</h3>
          <div className="flex items-end gap-1" style={{ height: '120px' }}>
            {metrics.daily.map((d: any, i: number) => {
              const maxItems = Math.max(...metrics.daily.map((x: any) => x.items), 1);
              const h = Math.max((d.items / maxItems) * 100, 2);
              return (
                <div key={i} className="flex-1 flex flex-col items-center group relative">
                  <div className="w-full bg-blue-500 rounded-t-sm" style={{ height: `${h}%` }} />
                  <span className="text-[10px] text-[var(--color-text-secondary)] mt-1 rotate-[-45deg] origin-top-left">
                    {new Date(d.date).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}
                  </span>
                  <div className="hidden group-hover:block absolute -top-6 bg-gray-900 text-white text-xs px-2 py-1 rounded">{d.items}</div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
